package io.jsonize.web.compare

import kotlinx.serialization.json.Json

data class LineDiff(
    val line: Int,
    val kind: LineDiffKind,
    val columnStart: Int? = null,
    val columnEnd: Int? = null,
    val offsetStart: Int? = null,
    val offsetEnd: Int? = null
)

enum class LineDiffKind {
    Added,
    Removed,
    Changed
}

data class LineDiffResult(
    val left: List<LineDiff>,
    val right: List<LineDiff>
)

fun calculateLineDiffs(
    leftText: String,
    rightText: String
): LineDiffResult {
    calculateJsonDiffsIfPossible(leftText, rightText)?.let { return it }

    val leftLines = splitLines(leftText)
    val rightLines = splitLines(rightText)

    if (leftLines.isEmpty() || rightLines.isEmpty()) {
        return LineDiffResult(
            left = leftLines.indices.map { idx ->
                LineDiff(idx + 1, LineDiffKind.Removed)
            },
            right = rightLines.indices.map { idx ->
                LineDiff(idx + 1, LineDiffKind.Added)
            }
        )
    }

    val maxCells = 2_000_000L
    val ops = if (leftLines.size.toLong() * rightLines.size.toLong() > maxCells) {
        diffByIndex(leftLines, rightLines)
    } else {
        diffByLcs(leftLines, rightLines)
    }

    return toLineDiffs(ops)
}

private fun calculateJsonDiffsIfPossible(
    leftText: String,
    rightText: String
): LineDiffResult? {
    if (leftText.isBlank() || rightText.isBlank()) return null

    val json = Json { ignoreUnknownKeys = true }
    val leftElement = runCatching { json.parseToJsonElement(leftText) }.getOrNull() ?: return null
    val rightElement = runCatching { json.parseToJsonElement(rightText) }.getOrNull() ?: return null

    val diff = diffJson(
        left = leftElement,
        right = rightElement,
        options = DiffOptions(ignoreArrayOrder = false)
    )
    if (diff.summary.total == 0) {
        return LineDiffResult(left = emptyList(), right = emptyList())
    }

    val leftSpans = mapJsonValueSpans(leftText)
    val rightSpans = mapJsonValueSpans(rightText)
    val leftDiffs = mutableListOf<LineDiff>()
    val rightDiffs = mutableListOf<LineDiff>()

    diff.entries.forEach { entry ->
        when (entry.kind) {
            DiffKind.Added -> {
                val span = rightSpans[entry.path] ?: return@forEach
                addLineRange(rightDiffs, span.startLine, span.endLine, LineDiffKind.Added)
            }
            DiffKind.Removed -> {
                val span = leftSpans[entry.path] ?: return@forEach
                addLineRange(leftDiffs, span.startLine, span.endLine, LineDiffKind.Removed)
            }
            DiffKind.Changed,
            DiffKind.TypeChanged -> {
                leftSpans[entry.path]?.let { span ->
                    leftDiffs.add(
                        LineDiff(
                            line = span.startLine,
                            kind = LineDiffKind.Changed,
                            offsetStart = span.startOffset,
                            offsetEnd = span.endOffset
                        )
                    )
                }
                rightSpans[entry.path]?.let { span ->
                    rightDiffs.add(
                        LineDiff(
                            line = span.startLine,
                            kind = LineDiffKind.Changed,
                            offsetStart = span.startOffset,
                            offsetEnd = span.endOffset
                        )
                    )
                }
            }
            DiffKind.Unchanged -> Unit
        }
    }

    return LineDiffResult(
        left = dedupeLineHighlights(leftDiffs),
        right = dedupeLineHighlights(rightDiffs)
    )
}

private fun splitLines(text: String): List<String> {
    if (text.isEmpty()) return emptyList()
    val normalized = text.replace("\r\n", "\n").replace("\r", "\n")
    val lines = mutableListOf<String>()
    var start = 0
    for (i in normalized.indices) {
        if (normalized[i] == '\n') {
            lines.add(normalized.substring(start, i))
            start = i + 1
        }
    }
    lines.add(normalized.substring(start))
    return lines
}

private enum class DiffOpKind { Equal, Insert, Delete }

private data class DiffOp(val kind: DiffOpKind, val value: String)

private fun diffByIndex(left: List<String>, right: List<String>): List<DiffOp> {
    val ops = mutableListOf<DiffOp>()
    val lookahead = 40
    var i = 0
    var j = 0
    while (i < left.size || j < right.size) {
        val l = left.getOrNull(i)
        val r = right.getOrNull(j)
        when {
            l == null && r != null -> {
                ops.add(DiffOp(DiffOpKind.Insert, r))
                j++
            }
            r == null && l != null -> {
                ops.add(DiffOp(DiffOpKind.Delete, l))
                i++
            }
            l == r -> {
                ops.add(DiffOp(DiffOpKind.Equal, l ?: ""))
                i++
                j++
            }
            else -> {
                var leftMatch = -1
                for (k in 1..lookahead) {
                    val idx = j + k
                    if (idx < right.size && right[idx] == l) {
                        leftMatch = k
                        break
                    }
                }
                var rightMatch = -1
                for (k in 1..lookahead) {
                    val idx = i + k
                    if (idx < left.size && left[idx] == r) {
                        rightMatch = k
                        break
                    }
                }
                when {
                    leftMatch == -1 && rightMatch == -1 -> {
                        ops.add(DiffOp(DiffOpKind.Delete, l ?: ""))
                        ops.add(DiffOp(DiffOpKind.Insert, r ?: ""))
                        i++
                        j++
                    }
                    rightMatch == -1 || (leftMatch != -1 && leftMatch <= rightMatch) -> {
                        ops.add(DiffOp(DiffOpKind.Insert, r ?: ""))
                        j++
                    }
                    else -> {
                        ops.add(DiffOp(DiffOpKind.Delete, l ?: ""))
                        i++
                    }
                }
            }
        }
    }
    return ops
}

private fun diffByLcs(left: List<String>, right: List<String>): List<DiffOp> {
    val n = left.size
    val m = right.size
    val dp = Array(n + 1) { IntArray(m + 1) }

    for (i in n - 1 downTo 0) {
        for (j in m - 1 downTo 0) {
            dp[i][j] = if (left[i] == right[j]) {
                dp[i + 1][j + 1] + 1
            } else {
                maxOf(dp[i + 1][j], dp[i][j + 1])
            }
        }
    }

    val ops = mutableListOf<DiffOp>()
    var i = 0
    var j = 0
    while (i < n && j < m) {
        if (left[i] == right[j]) {
            ops.add(DiffOp(DiffOpKind.Equal, left[i]))
            i++
            j++
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            ops.add(DiffOp(DiffOpKind.Delete, left[i]))
            i++
        } else {
            ops.add(DiffOp(DiffOpKind.Insert, right[j]))
            j++
        }
    }
    while (i < n) {
        ops.add(DiffOp(DiffOpKind.Delete, left[i]))
        i++
    }
    while (j < m) {
        ops.add(DiffOp(DiffOpKind.Insert, right[j]))
        j++
    }

    return ops
}

private fun toLineDiffs(ops: List<DiffOp>): LineDiffResult {
    val leftDiffs = mutableListOf<LineDiff>()
    val rightDiffs = mutableListOf<LineDiff>()

    var leftLine = 1
    var rightLine = 1
    var idx = 0
    while (idx < ops.size) {
        val op = ops[idx]
        if (op.kind == DiffOpKind.Equal) {
            leftLine++
            rightLine++
            idx++
            continue
        }

        val deletes = mutableListOf<String>()
        val inserts = mutableListOf<String>()
        while (idx < ops.size && ops[idx].kind != DiffOpKind.Equal) {
            when (ops[idx].kind) {
                DiffOpKind.Delete -> deletes.add(ops[idx].value)
                DiffOpKind.Insert -> inserts.add(ops[idx].value)
                DiffOpKind.Equal -> Unit
            }
            idx++
        }

        val pairCount = minOf(deletes.size, inserts.size)
        for (offset in deletes.indices) {
            if (offset < pairCount) {
                val (leftRange, rightRange) = diffRanges(deletes[offset], inserts[offset])
                leftDiffs.add(
                    LineDiff(
                        line = leftLine + offset,
                        kind = LineDiffKind.Changed,
                        columnStart = leftRange?.start,
                        columnEnd = leftRange?.end
                    )
                )
                rightDiffs.add(
                    LineDiff(
                        line = rightLine + offset,
                        kind = LineDiffKind.Changed,
                        columnStart = rightRange?.start,
                        columnEnd = rightRange?.end
                    )
                )
            } else {
                leftDiffs.add(LineDiff(leftLine + offset, LineDiffKind.Removed))
            }
        }
        for (offset in pairCount until inserts.size) {
            rightDiffs.add(LineDiff(rightLine + offset, LineDiffKind.Added))
        }

        leftLine += deletes.size
        rightLine += inserts.size
    }

    return LineDiffResult(left = leftDiffs, right = rightDiffs)
}

private data class DiffRange(val start: Int, val end: Int)

private fun diffRanges(left: String, right: String): Pair<DiffRange?, DiffRange?> {
    if (left == right) return null to null

    val maxStart = minOf(left.length, right.length)
    var start = 0
    while (start < maxStart && left[start] == right[start]) {
        start++
    }

    var leftEnd = left.length
    var rightEnd = right.length
    while (leftEnd > start && rightEnd > start &&
        left[leftEnd - 1] == right[rightEnd - 1]
    ) {
        leftEnd--
        rightEnd--
    }

    val leftRange = if (start < leftEnd) DiffRange(start, leftEnd) else null
    val rightRange = if (start < rightEnd) DiffRange(start, rightEnd) else null
    return leftRange to rightRange
}

private data class JsonValueSpan(
    val startOffset: Int,
    val endOffset: Int,
    val startLine: Int,
    val startColumn: Int,
    val endLine: Int,
    val endColumn: Int
)

private fun mapJsonValueSpans(text: String): Map<String, JsonValueSpan> {
    return JsonSpanParser(text).parse()
}

private fun addLineRange(
    target: MutableList<LineDiff>,
    startLine: Int,
    endLine: Int,
    kind: LineDiffKind
) {
    val safeStart = startLine.coerceAtLeast(1)
    val safeEnd = endLine.coerceAtLeast(safeStart)
    for (line in safeStart..safeEnd) {
        target.add(LineDiff(line = line, kind = kind))
    }
}

private fun dedupeLineHighlights(diffs: List<LineDiff>): List<LineDiff> {
    val seen = mutableSetOf<Pair<Int, LineDiffKind>>()
    val result = mutableListOf<LineDiff>()
    diffs.forEach { diff ->
        if (diff.kind == LineDiffKind.Changed) {
            result.add(diff)
        } else if (seen.add(diff.line to diff.kind)) {
            result.add(diff)
        }
    }
    return result
}

private fun escapePointer(segment: String): String {
    return segment.replace("~", "~0").replace("/", "~1")
}

private class JsonSpanParser(private val text: String) {
    private val length = text.length
    private var index = 0
    private var line = 1
    private var column = 1
    private val spans = mutableMapOf<String, JsonValueSpan>()

    fun parse(): Map<String, JsonValueSpan> {
        skipWhitespace()
        parseValue("")
        return spans
    }

    private fun parseValue(path: String) {
        skipWhitespace()
        val startOffset = index
        val startLine = line
        val startColumn = column

        when (peek()) {
            '{' -> parseObject(path)
            '[' -> parseArray(path)
            '"' -> parseStringValue()
            '-', in '0'..'9' -> parseNumber()
            't' -> parseLiteral("true")
            'f' -> parseLiteral("false")
            'n' -> parseLiteral("null")
            else -> return
        }

        val endOffset = index
        val endLine = line
        val endColumn = column
        spans[path] = JsonValueSpan(
            startOffset = startOffset,
            endOffset = endOffset,
            startLine = startLine,
            startColumn = startColumn,
            endLine = endLine,
            endColumn = endColumn
        )
    }

    private fun parseObject(path: String) {
        expectChar('{')
        skipWhitespace()
        if (peek() == '}') {
            advance()
            return
        }
        while (index < length) {
            skipWhitespace()
            val key = parseString()
            skipWhitespace()
            expectChar(':')
            val childPath = if (path.isEmpty()) {
                "/${escapePointer(key)}"
            } else {
                "$path/${escapePointer(key)}"
            }
            parseValue(childPath)
            skipWhitespace()
            when (peek()) {
                ',' -> {
                    advance()
                }
                '}' -> {
                    advance()
                    break
                }
                else -> break
            }
        }
    }

    private fun parseArray(path: String) {
        expectChar('[')
        skipWhitespace()
        if (peek() == ']') {
            advance()
            return
        }
        var idx = 0
        while (index < length) {
            val childPath = if (path.isEmpty()) "/$idx" else "$path/$idx"
            parseValue(childPath)
            idx++
            skipWhitespace()
            when (peek()) {
                ',' -> advance()
                ']' -> {
                    advance()
                    break
                }
                else -> break
            }
        }
    }

    private fun parseStringValue() {
        parseString()
    }

    private fun parseString(): String {
        val sb = StringBuilder()
        expectChar('"')
        while (index < length) {
            val ch = advance()
            when (ch) {
                '"' -> break
                '\\' -> {
                    if (index >= length) break
                    val esc = advance()
                    when (esc) {
                        '"', '\\', '/' -> sb.append(esc)
                        'b' -> sb.append('\b')
                        'f' -> sb.append('\u000C')
                        'n' -> sb.append('\n')
                        'r' -> sb.append('\r')
                        't' -> sb.append('\t')
                        'u' -> sb.append(readUnicode())
                        else -> sb.append(esc)
                    }
                }
                else -> sb.append(ch)
            }
        }
        return sb.toString()
    }

    private fun readUnicode(): Char {
        var value = 0
        repeat(4) {
            val ch = if (index < length) advance() else '0'
            value = (value shl 4) + hexValue(ch)
        }
        return value.toChar()
    }

    private fun parseNumber() {
        if (peek() == '-') advance()
        while (peek()?.isDigit() == true) {
            advance()
        }
        if (peek() == '.') {
            advance()
            while (peek()?.isDigit() == true) {
                advance()
            }
        }
        val exp = peek()
        if (exp == 'e' || exp == 'E') {
            advance()
            val sign = peek()
            if (sign == '+' || sign == '-') {
                advance()
            }
            while (peek()?.isDigit() == true) {
                advance()
            }
        }
    }

    private fun parseLiteral(value: String) {
        value.forEach { expected ->
            if (peek() == expected) {
                advance()
            } else {
                return
            }
        }
    }

    private fun skipWhitespace() {
        while (index < length && text[index].isWhitespace()) {
            advance()
        }
    }

    private fun peek(): Char? = if (index < length) text[index] else null

    private fun advance(): Char {
        val ch = text[index++]
        if (ch == '\n') {
            line++
            column = 1
        } else {
            column++
        }
        return ch
    }

    private fun expectChar(expected: Char) {
        if (peek() == expected) {
            advance()
        }
    }

    private fun hexValue(ch: Char): Int {
        return when (ch) {
            in '0'..'9' -> ch.code - '0'.code
            in 'a'..'f' -> ch.code - 'a'.code + 10
            in 'A'..'F' -> ch.code - 'A'.code + 10
            else -> 0
        }
    }
}
